import {
  BASEMAP_LAYERS,
  BASEMAP_SOURCE_ID,
  NIGHT_THEME,
  PAPER_THEME,
  mapPaletteFor,
} from "@festipal/shared";
import { addProtocol, setWorkerUrl, type StyleSpecification } from "maplibre-gl";
// MapLibre 6 looks for its worker next to its own module, at an address worked
// out at run time that Vite cannot see: the file never made it into the build,
// and every map of the back-office lost its background to a 404. Vite bundles
// the worker here and hands over its address.
import workerUrl from "maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url";
import { PMTiles, Protocol } from "pmtiles";

/**
 * Fond de carte du back-office.
 *
 * La même archive que l'application mobile, servie par le serveur du
 * back-office : le calage se fait donc sur la carte que verront les
 * festivaliers, et rien n'est demandé à un service extérieur.
 */

const ARCHIVE_URL = `${window.location.origin}/basemap.pmtiles`;

let registered = false;

/**
 * Fond de carte accordé au thème du système.
 *
 * Un fond crème dans une interface sombre trancherait, et l'organisateur
 * calerait son plan sur une carte qui ne ressemble pas à celle des festivaliers
 * en pareil réglage.
 */
function activeTheme() {
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? NIGHT_THEME
    : PAPER_THEME;
}

/** Le protocole ne s'enregistre qu'une fois pour toute la page. */
function register() {
  if (registered) return;
  setWorkerUrl(workerUrl);
  addProtocol("pmtiles", new Protocol().tile);
  registered = true;
}

export type Basemap = {
  style: StyleSpecification;
  /** Vue initiale, tirée de l'archive elle-même. */
  center: [number, number];
  zoom: number;
};

/** Style dépourvu de fond, seul repli possible si l'archive manque. */
function emptyBasemap(): Basemap {
  return {
    style: {
      version: 8,
      sources: {},
      layers: [
        {
          id: "fond",
          type: "background",
          paint: { "background-color": mapPaletteFor(activeTheme()).background },
        },
      ],
    },
    center: [0, 0],
    zoom: 2,
  };
}

/**
 * La vue initiale vient de l'en-tête de l'archive, et non d'un point codé en
 * dur : l'extrait sait où il se trouve, alors qu'un festival n'a pas encore de
 * coordonnées en base.
 */
export async function loadBasemap(): Promise<Basemap> {
  register();

  try {
    const header = await new PMTiles(ARCHIVE_URL).getHeader();
    return {
      style: {
        version: 8,
        sources: {
          [BASEMAP_SOURCE_ID]: { type: "vector", url: `pmtiles://${ARCHIVE_URL}` },
        },
        layers: [
          {
            id: "fond",
            type: "background",
            paint: { "background-color": mapPaletteFor(activeTheme()).background },
          },
          ...(BASEMAP_LAYERS as unknown as StyleSpecification["layers"]),
        ],
      },
      center: [header.centerLon, header.centerLat],
      zoom: Math.min(header.maxZoom - 1, 14),
    };
  } catch {
    return emptyBasemap();
  }
}
